import { flatten, isArray, omit, pick, snakeCase } from 'lodash';
import { track } from '~/utils/segment';
import {
  ADDRESS_TRACKING_EVENT,
  CARRIER_ACCOUNT_TRACKING_EVENT,
  LABEL_PURCHASED_EVENT,
  ORDER_TRACKING_EVENT,
  OperationsForEvent,
  PAYMENT_METHOD_EVENT,
  RequiredPropsForOperation,
  TrackingAppLocation,
  TrackingEventName,
  TrackingReference,
  PACKAGE_TRACKING_EVENT,
  ITEM_UPDATED_EVENT,
  MANIFEST_TRACKING_EVENT,
  TrackedOrderDetails,
  LABEL_UPDATED_EVENT,
  PARCEL_BOT_TRACKING_EVENT,
  LabelDownloadedTrackingType,
  DEFAULT_UPDATED_EVENT,
  AllTrackedProps,
  BULK_BAR_TRACKING_EVENT,
  RATE_DISPLAYED_EVENT,
  IBD_MODAL_TRACKING_EVENT,
  RECIPIENT_ADDRESS_BOOK_UPDATED,
  RECIPIENT_ADDRESS_BOOK_QUERIED,
  API_TOKEN_UPDATED,
} from './types';

import { trackingHelper } from '.';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { OrderObjectId, OrderSelection } from '~/types/Order';
import Rate, { RateDisplayAttribute } from '~/types/Rate';
import CarrierAccount from '~/types/CarrierAccount';

type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnakeCase<U>}`
  : S;

type KeysToSnakeCase<T> = {
  [K in keyof T as CamelToSnakeCase<string & K>]: T[K];
};

// type SnakeCaseProperties = KeysToSnakeCase<ReturnType<typeof trackingHelper['properties']>>;

export function objectToSnake<T extends object>(input: T): KeysToSnakeCase<T> {
  return Object.entries(input).reduce<KeysToSnakeCase<T>>((accum, curr) => {
    accum[snakeCase(curr[0])] = curr[1];
    return accum;
  }, {} as KeysToSnakeCase<T>);
}

interface RequestUpdatedTrackPayload {
  url: string;
  isError: boolean;
  status: number;
  message: string | undefined;
}

export const requestUpdatedTrack = <T>(
  trackingReference: TrackingReference,
  { url, isError, status, message }: RequestUpdatedTrackPayload,
  additionalProps?: T,
) => {
  const properties = {
    url,
    isError,
    status,
    message,
    ...additionalProps,
  };

  const propsSet = applyTransformsForTrackedProps(trackingReference, properties);
  propsSet.forEach(() => {
    // @NOTE Event is disabled for now to lower usage.
    // track('Request Updated', props);
  });
};

export function trackRequestedUpdatedAxiosError(
  trackingReference: TrackingReference,
  error: AxiosError,
) {
  if (axios.isAxiosError(error) && error.response) {
    const { response } = error;
    requestUpdatedTrack(trackingReference, {
      url: response.config.url || '',
      isError: response.status >= 300,
      status: response.status,
      message: error.message,
    });
  }
}

export function trackRequestedUpdatedResponse(
  trackingReference: TrackingReference,
  response: AxiosResponse<unknown> | undefined,
) {
  if (!response || !response.config || !response.status) {
    return;
  }
  requestUpdatedTrack(trackingReference, {
    url: response.config.url || '',
    isError: response.status >= 300,
    status: response.status,
    message: undefined,
  });
}

export const trackWithGlobalProps = <T extends object>(
  appLocation: TrackingAppLocation,
  event: string,
  input?: T,
) => {
  const properties = omitNestedObjects({
    ...trackingHelper.appendGlobalProps(input),
    appLocation,
  });
  const propertiesAsSnakeCase = objectToSnake(properties);

  track(event, propertiesAsSnakeCase);
};

export const getShipmentIdsByOrderId = (
  trackingReference: TrackingReference,
  orderId: OrderObjectId,
): number[] => {
  if (trackingReference.selectedOrders) {
    return trackingReference.selectedOrders[orderId] || [];
  }

  return [];
};

type TrackedShipmentProps = {
  orderId: string;
  orderProcessingId: number;
  batchCount: number;
} & TrackedOrderDetails;

const getShipmentProps = (
  orderId: string,
  orderProcessingId: number,
  batchCount: number,
  trackedOrderDetailsByShipment?: Record<number, TrackedOrderDetails>,
): TrackedShipmentProps => {
  if (!trackedOrderDetailsByShipment) {
    return {
      orderId,
      orderProcessingId,
      batchCount,
    };
  }

  const trackedOrderDetails = trackedOrderDetailsByShipment[orderProcessingId];
  return {
    orderId,
    orderProcessingId,
    batchCount,
    ...trackedOrderDetails,
  };
};

export const getOrdersPropsSet = (
  trackingReference: TrackingReference,
): Partial<AllTrackedProps>[] => {
  if (trackingReference.orderIds && trackingReference.orderIds?.length > 0) {
    const shipmentIdsAppliedToOrderIds = trackingReference.orderIds.map((orderId) => {
      const shipmentIds = getShipmentIdsByOrderId(trackingReference, orderId);
      if (shipmentIds.length === 0) {
        return [{ orderId }];
      }

      const batchCount = shipmentIds.length;
      return shipmentIds.map((shipmentId) =>
        getShipmentProps(
          orderId,
          shipmentId,
          batchCount,
          trackingReference.trackedOrderDetailsByShipment,
        ),
      );
    });

    return flatten(shipmentIdsAppliedToOrderIds);
  }

  if (trackingReference.orderId) {
    const orderId = trackingReference.orderId;
    const shipmentIds = getShipmentIdsByOrderId(trackingReference, orderId);
    const batchCount = shipmentIds.length;
    if (batchCount === 0) {
      return [{ orderId }];
    } else {
      return shipmentIds.map((shipmentId) =>
        getShipmentProps(
          orderId,
          shipmentId,
          batchCount,
          trackingReference.trackedOrderDetailsByShipment,
        ),
      );
    }
  }

  return [];
};

export const getAdditionalPropsSet = (
  trackingReference: TrackingReference,
): Partial<AllTrackedProps>[] => {
  const ordersPropsSet = getOrdersPropsSet(trackingReference);
  return ordersPropsSet.map((orderProps) => ({
    ...orderProps,
  }));
};

export const isNestedObject = (values: object | number | string | boolean | null | undefined) =>
  !!values && !!Object.values(values).find((v) => typeof v === 'object');

export const omitNestedObjects = (values: object) =>
  pick(
    values,
    Object.keys(values).filter((key) => !isNestedObject(values[key])),
  );

export const applyTransformsForTrackedProps = <P>(
  trackingReference: TrackingReference,
  properties?: P,
): object[] => {
  const transmittedTrackingReference = omit(
    trackingReference,
    'selectedOrders',
    'trackedOrderDetailsByShipment',
    'trackedUserSettings',
    'userDetails',
  );

  const referencePropertiesAsSnakeCase = objectToSnake(transmittedTrackingReference);
  const propertiesAsSnakeCase = objectToSnake(properties || {});

  const additionalPropsSet = getAdditionalPropsSet(trackingReference);
  if (additionalPropsSet.length > 0) {
    return additionalPropsSet.map((additionalProps) => {
      const additionalPropsAsSnakeCase = objectToSnake(additionalProps);
      return omitNestedObjects({
        ...referencePropertiesAsSnakeCase,
        ...additionalPropsAsSnakeCase,
        ...propertiesAsSnakeCase,
      });
    });
  }

  return [
    omitNestedObjects({
      ...referencePropertiesAsSnakeCase,
      ...propertiesAsSnakeCase,
    }),
  ];
};

const labelPurchasedEvent = <N extends TrackingEventName>(
  eventName: N,
  operation: OperationsForEvent<N>,
  propsSet: Record<string, string>[],
) => {
  if (
    (eventName === LABEL_PURCHASED_EVENT.name &&
      operation === LABEL_PURCHASED_EVENT.operations.SAVE_ATTEMPTED) ||
    operation === LABEL_PURCHASED_EVENT.operations.TRANSACTION_COMPLETED
  ) {
    return propsSet.map((set, idx) =>
      set.rate_ids ? { ...set, rate_id: set.rate_ids[idx] } : { ...set },
    );
  }
  return propsSet;
};

/**
 * @description add custom properties by targeting event name. this can be helpful when adding properties to individual orders in an array (propsSet).
 */
const addCustomPropsByEventName = <N extends TrackingEventName>(
  eventName: N,
  operation: OperationsForEvent<N>,
  propsSet: Record<string, string>[],
) => {
  switch (eventName) {
    case LABEL_PURCHASED_EVENT.name:
      return labelPurchasedEvent(eventName, operation, propsSet);
    default:
      return propsSet;
  }
};

export const advancedTrack = <N extends TrackingEventName, P>(
  eventName: N,
  operation: OperationsForEvent<N>,
  trackingReference: TrackingReference,
  properties?: P,
): void => {
  // propsSet length is defined by the number of orderIds in trackingReference, each propsSet will fire a tracking event.
  let propsSet = applyTransformsForTrackedProps(trackingReference, properties) as Record<
    string,
    string
  >[];
  propsSet = addCustomPropsByEventName(eventName, operation, propsSet);

  propsSet.forEach((props) => {
    track(eventName, {
      ...props,
      operation,
    });
  });

  try {
    if (
      !!trackingHelper.fullStoryEvent &&
      eventName === ORDER_TRACKING_EVENT.name &&
      operation === ORDER_TRACKING_EVENT.operations.VIEWED
    ) {
      trackingHelper.fullStoryEvent(
        `${ORDER_TRACKING_EVENT.name} ${ORDER_TRACKING_EVENT.operations.VIEWED}`,
        {
          order_id: trackingReference.orderId || '',
          order_ids: trackingReference.orderIds || [],
          reference_id: trackingReference.referenceId,
        },
      );
    }
  } catch {
    // ignore it
  }
};

export const trackingFunctionFactory = <E extends TrackingEventName>(trackingEventName: E) => {
  return <T extends TrackingReference, O extends OperationsForEvent<E>, P extends object>(
    trackingReference: T,
    operation: O,
    additionalProps: RequiredPropsForOperation<E, O> extends never
      ? P
      : RequiredPropsForOperation<E, O> & P,
  ) => {
    advancedTrack(trackingEventName, operation, trackingReference, additionalProps);
  };
};

export const trackCarrierAccountUpdate = trackingFunctionFactory(
  CARRIER_ACCOUNT_TRACKING_EVENT.name,
);
// @NOTE Event is disabled for now to lower usage.
export const trackOrderUpdate = (_1, _2, _3) => {}; // trackingFunctionFactory(ORDER_TRACKING_EVENT.name);
export const trackAddressUpdate = trackingFunctionFactory(ADDRESS_TRACKING_EVENT.name);
export const trackLabelPurchased = trackingFunctionFactory(LABEL_PURCHASED_EVENT.name);
export const trackPaymentMethodUpdate = trackingFunctionFactory(PAYMENT_METHOD_EVENT.name);
export const trackPackageUpdate = trackingFunctionFactory(PACKAGE_TRACKING_EVENT.name);
// @NOTE Event is disabled for now to lower usage.
export const trackRateUpdate = (_1, _2, _3) => {}; // trackingFunctionFactory(RATES_UPDATED_EVENT.name);
export const trackRateDisplay = trackingFunctionFactory(RATE_DISPLAYED_EVENT.name);
export const trackItemUpdated = trackingFunctionFactory(ITEM_UPDATED_EVENT.name);
export const trackManifestUpdated = trackingFunctionFactory(MANIFEST_TRACKING_EVENT.name);
export const trackLabelUpdated = trackingFunctionFactory(LABEL_UPDATED_EVENT.name);
export const trackParcelBotUpdated = trackingFunctionFactory(PARCEL_BOT_TRACKING_EVENT.name);
export const trackDefaultUpdated = trackingFunctionFactory(DEFAULT_UPDATED_EVENT.name);
export const trackBulkBar = trackingFunctionFactory(BULK_BAR_TRACKING_EVENT.name);
export const trackIBDModal = trackingFunctionFactory(IBD_MODAL_TRACKING_EVENT.name);
export const trackRecipientAddressBookUpdated = trackingFunctionFactory(
  RECIPIENT_ADDRESS_BOOK_UPDATED.name,
);
export const trackRecipientAddressBookQueried = trackingFunctionFactory(
  RECIPIENT_ADDRESS_BOOK_QUERIED.name,
);
export const trackAPITokenUpdated = trackingFunctionFactory(API_TOKEN_UPDATED.name);
export function getGenericErrorsFromResponseData(data?: unknown) {
  if (!data) {
    return undefined;
  }

  if (data.hasOwnProperty('non_field_errors')) {
    const nonFieldErrors = data as unknown;
    if (isArray(nonFieldErrors)) {
      return nonFieldErrors[0];
    }
  }
}

export const trackDownload = (
  appLocation: TrackingAppLocation,
  event: LabelDownloadedTrackingType,
  orderSelection: OrderSelection,
) => {
  const trackingReference = trackingHelper.getTrackingReference(
    LABEL_UPDATED_EVENT.name,
    appLocation,
    { type: event },
  );

  const trackingReferenceWithOrderIds = trackingHelper.amendTrackingReferenceOrderSelection(
    trackingReference,
    orderSelection,
  );

  trackLabelUpdated(trackingReferenceWithOrderIds, LABEL_UPDATED_EVENT.operations.DOWNLOADED, {});
};

export type RateDisplayedEventDataType = {
  rate_id: string;
  carrier: string;
  service_level: string;
  price: string;
  transit_time_in_days: number;
  rank: number;
  tag: string;
  carrier_account_id?: string;
};

export const getRateDisplayedEventData = (
  rate: Rate,
  sortedRates: {
    displayAttribute: RateDisplayAttribute;
    rates: Rate[];
  }[],
  carrierAccounts: CarrierAccount[],
): RateDisplayedEventDataType => {
  const {
    amount,
    objectId,
    provider,
    estimatedDays,
    servicelevel: { name: serviceLevelName },
    carrierAccount,
  } = rate;

  const { accountId } =
    carrierAccounts.find((account) => account.objectId === carrierAccount) || {};

  const rateIds = sortedRates.map((entry) => entry.rates.map((rate) => rate.objectId)).flat();
  const rank = rateIds.indexOf(objectId) + 1;

  const tag =
    sortedRates.find((entry) => entry.rates.map((rate) => rate.objectId).includes(objectId))
      ?.displayAttribute || '';

  return {
    rate_id: objectId,
    carrier: provider,
    service_level: serviceLevelName,
    price: amount,
    transit_time_in_days: estimatedDays,
    rank,
    tag,
    carrier_account_id: accountId,
  };
};
